import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  generatePassword,
  calculatePasswordEntropy,
  setMasterPin,
  removeMasterPin,
  isVaultLocked,
  lockVault,
  unlockVault,
  auditVaultCredentials,
  encryptSecret,
  isEncrypted,
} from '../lib/vaultCrypto';
import { playSound } from '../lib/soundEffects';

const {
  FiShield, FiKey, FiLock, FiUnlock, FiRefreshCw, FiCopy,
  FiCheck, FiX, FiAlertTriangle, FiCheckCircle2, FiDownload, FiZap,
} = FiIcons;

export default function VaultSecurityModal({
  isOpen,
  onClose,
  data = {},
  onSaveBookmark,
  userId = 'default_user',
  themeColors: tc,
  isDark = true,
}) {
  const [activeTab, setActiveTab] = useState('generator'); // 'generator' | 'lock' | 'audit'

  // Generator State
  const [pwdLength, setPwdLength] = useState(18);
  const [useUpper, setUseUpper] = useState(true);
  const [useLower, setUseLower] = useState(true);
  const [useNums, setUseNums] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [generatedPwd, setGeneratedPwd] = useState(() =>
    generatePassword({ length: 18, uppercase: true, lowercase: true, numbers: true, symbols: true })
  );
  const [copied, setCopied] = useState(false);

  // Master PIN State
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [hasPinSet, setHasPinSet] = useState(() => !!localStorage.getItem('memark_has_master_pin'));
  const [isLocked, setIsLocked] = useState(() => isVaultLocked());

  // Migration State
  const [migrating, setMigrating] = useState(false);
  const [migrationDone, setMigrationDone] = useState(false);

  const pwdEntropy = useMemo(() => calculatePasswordEntropy(generatedPwd), [generatedPwd]);
  const vaultAudit = useMemo(() => auditVaultCredentials(data), [data]);

  const handleRegenerate = () => {
    playSound('click');
    const pwd = generatePassword({
      length: pwdLength,
      uppercase: useUpper,
      lowercase: useLower,
      numbers: useNums,
      symbols: useSymbols,
    });
    setGeneratedPwd(pwd);
  };

  const handleCopy = () => {
    playSound('copy');
    navigator.clipboard.writeText(generatedPwd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSavePin = () => {
    if (pinInput.trim().length < 4) {
      setPinError('PIN must be at least 4 characters');
      return;
    }
    playSound('lock');
    setMasterPin(pinInput.trim());
    setHasPinSet(true);
    setPinInput('');
    setPinError('');
  };

  const handleRemovePin = () => {
    playSound('click');
    removeMasterPin();
    setHasPinSet(false);
    setPinInput('');
    setIsLocked(false);
  };

  const handleToggleLock = () => {
    if (isLocked) {
      playSound('unlock');
      unlockVault(pinInput || 'unlocked');
      setIsLocked(false);
    } else {
      playSound('lock');
      lockVault();
      setIsLocked(true);
    }
  };

  const handleBatchEncrypt = async () => {
    if (!onSaveBookmark) return;
    setMigrating(true);
    try {
      for (const item of vaultAudit.unencryptedItems) {
        if (!isEncrypted(item.credentials)) {
          const enc = await encryptSecret(item.credentials, userId);
          await onSaveBookmark(item.id, { credentials: enc });
        }
      }
      playSound('success');
      setMigrationDone(true);
    } catch (err) {
      console.error('Batch encrypt error:', err);
    } finally {
      setMigrating(false);
    }
  };

  const handleExportVault = () => {
    playSound('launch');
    const sensitiveExport = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      vaultCipher: 'AES-GCM-256',
      items: Object.values(data).flatMap((f) => f.items || []).filter((i) => i.credentials?.trim()),
    };
    const blob = new Blob([JSON.stringify(sensitiveExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memark-encrypted-vault-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-lg rounded-2xl bg-[#121216] border border-[#26262e] shadow-2xl p-6 z-10 max-h-[88vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[#24242c] shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                <SafeIcon icon={FiShield} className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <span>Zero-Knowledge Vault</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                    AES-GCM-256
                  </span>
                </h3>
                <p className="text-xs text-[#777]">Client-side encrypted credentials with zero server plaintext</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[#666] hover:text-white hover:bg-[#222] transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Tab Selector */}
          <div className="flex items-center gap-1.5 p-1 bg-[#1a1a20] rounded-xl my-4 border border-[#272730] shrink-0">
            {[
              { id: 'generator', label: 'Password Generator', icon: FiKey },
              { id: 'lock', label: 'Vault Lock & PIN', icon: FiLock },
              { id: 'audit', label: 'Security Health', icon: FiShield },
            ].map(({ id, label, icon }) => (
              <button
                key={id}
                onClick={() => { playSound('click'); setActiveTab(id); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === id
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
                    : 'text-[#888] hover:text-white hover:bg-white/5'
                }`}
              >
                <SafeIcon icon={icon} className="w-3.5 h-3.5" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* TAB 1: Password Generator */}
            {activeTab === 'generator' && (
              <div className="space-y-4">
                {/* Generated Password Box */}
                <div className="p-4 rounded-xl bg-[#17171e] border border-[#292934] relative group">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wider text-[#777] font-semibold">
                      Generated Password
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                        style={{ backgroundColor: `${pwdEntropy.color}20`, color: pwdEntropy.color, border: `1px solid ${pwdEntropy.color}40` }}>
                        {pwdEntropy.label} ({pwdEntropy.entropy} bits)
                      </span>
                    </div>
                  </div>

                  <div className="font-mono text-base font-semibold text-white tracking-wide break-all select-all py-1">
                    {generatedPwd}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-[#23232c]">
                    <button
                      onClick={handleRegenerate}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#aaa] hover:text-white bg-[#22222c] hover:bg-[#2b2b38] transition-colors flex items-center gap-1.5"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
                      <span>Regenerate</span>
                    </button>
                    <button
                      onClick={handleCopy}
                      className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-black bg-cyan-400 hover:bg-cyan-300 transition-colors flex items-center gap-1.5 shadow-lg shadow-cyan-400/20"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                      <span>{copied ? 'Copied (Auto-clear in 30s)' : 'Copy Password'}</span>
                    </button>
                  </div>
                </div>

                {/* Length Slider */}
                <div className="p-3.5 rounded-xl bg-[#17171e] border border-[#24242e] space-y-2">
                  <div className="flex items-center justify-between text-xs text-[#aaa]">
                    <span>Password Length</span>
                    <span className="font-mono font-bold text-cyan-400 text-sm">{pwdLength}</span>
                  </div>
                  <input
                    type="range"
                    min="8"
                    max="48"
                    value={pwdLength}
                    onChange={(e) => {
                      setPwdLength(Number(e.target.value));
                      setGeneratedPwd(generatePassword({
                        length: Number(e.target.value),
                        uppercase: useUpper,
                        lowercase: useLower,
                        numbers: useNums,
                        symbols: useSymbols,
                      }));
                    }}
                    className="w-full accent-cyan-400 cursor-pointer"
                  />
                </div>

                {/* Character Sets Toggles */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { label: 'Uppercase (A-Z)', val: useUpper, set: setUseUpper },
                    { label: 'Lowercase (a-z)', val: useLower, set: setUseLower },
                    { label: 'Numbers (0-9)', val: useNums, set: setUseNums },
                    { label: 'Symbols (!@#$%)', val: useSymbols, set: setUseSymbols },
                  ].map(({ label, val, set }, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        set(!val);
                        handleRegenerate();
                      }}
                      className={`p-2.5 rounded-xl border flex items-center justify-between transition-all ${
                        val
                          ? 'bg-cyan-500/10 border-cyan-500/30 text-white'
                          : 'bg-[#16161c] border-[#22222a] text-[#666]'
                      }`}
                    >
                      <span>{label}</span>
                      <SafeIcon icon={val ? FiCheck : FiX} className={`w-3.5 h-3.5 ${val ? 'text-cyan-400' : 'text-[#555]'}`} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 2: Vault Lock & PIN */}
            {activeTab === 'lock' && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[#17171e] border border-[#262630] space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-white">Master Vault PIN Protection</div>
                      <div className="text-xs text-[#777]">Lock sensitive credentials behind an extra PIN challenge</div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${hasPinSet ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[#222] text-[#666]'}`}>
                      {hasPinSet ? 'PIN Enabled' : 'Not Configured'}
                    </span>
                  </div>

                  {!hasPinSet ? (
                    <div className="space-y-2 pt-2">
                      <input
                        type="password"
                        placeholder="Enter master PIN or passphrase..."
                        value={pinInput}
                        onChange={(e) => setPinInput(e.target.value)}
                        className="w-full text-xs rounded-xl p-2.5 bg-[#101014] border border-[#2b2b36] text-white focus:outline-none focus:border-cyan-400"
                      />
                      {pinError && <div className="text-xs text-red-400">{pinError}</div>}
                      <button
                        onClick={handleSavePin}
                        className="w-full py-2 rounded-xl text-xs font-semibold text-black bg-cyan-400 hover:bg-cyan-300 transition-colors"
                      >
                        Enable Master PIN
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center justify-between p-3 rounded-xl bg-[#111116] border border-[#22222a]">
                        <span className="text-xs text-[#aaa]">Vault Status:</span>
                        <button
                          onClick={handleToggleLock}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                            isLocked ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          }`}
                        >
                          <SafeIcon icon={isLocked ? FiLock : FiUnlock} className="w-3.5 h-3.5" />
                          <span>{isLocked ? 'Locked' : 'Unlocked'}</span>
                        </button>
                      </div>

                      <button
                        onClick={handleRemovePin}
                        className="w-full py-2 rounded-xl text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 transition-colors"
                      >
                        Remove Master PIN
                      </button>
                    </div>
                  )}
                </div>

                {/* Encrypted Export */}
                <div className="p-4 rounded-xl bg-[#17171e] border border-[#262630] flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-white">Export Encrypted Vault</div>
                    <div className="text-xs text-[#777]">Download an offline backup of all encrypted bookmarks</div>
                  </div>
                  <button
                    onClick={handleExportVault}
                    className="px-3.5 py-2 rounded-xl text-xs font-semibold text-white bg-[#24242e] hover:bg-[#2d2d3a] border border-[#333] transition-colors flex items-center gap-1.5"
                  >
                    <SafeIcon icon={FiDownload} className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Backup Vault</span>
                  </button>
                </div>
              </div>
            )}

            {/* TAB 3: Security Health Audit */}
            {activeTab === 'audit' && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[#17171e] border border-[#262630] space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-white">Vault Health Overview</div>
                      <div className="text-xs text-[#777]">Encrypted storage status across all library credentials</div>
                    </div>
                    <span className="text-base font-mono font-bold text-cyan-400">
                      {vaultAudit.vaultEncryptedPercentage}% Encrypted
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs pt-1">
                    <div className="p-3 rounded-xl bg-[#121217] border border-[#222]">
                      <div className="text-[#777]">Saved Credentials</div>
                      <div className="text-lg font-bold text-white">{vaultAudit.totalWithCreds}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-[#121217] border border-[#222]">
                      <div className="text-[#777]">Unencrypted Legacy</div>
                      <div className="text-lg font-bold text-amber-400">{vaultAudit.unencryptedCount}</div>
                    </div>
                  </div>

                  {vaultAudit.unencryptedCount > 0 && !migrationDone && (
                    <div className="pt-2">
                      <button
                        onClick={handleBatchEncrypt}
                        disabled={migrating}
                        className="w-full py-2.5 rounded-xl text-xs font-semibold text-black bg-emerald-400 hover:bg-emerald-300 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-emerald-400/20"
                      >
                        <SafeIcon icon={FiZap} className="w-3.5 h-3.5" />
                        <span>{migrating ? 'Encrypting Vault...' : `Encrypt All ${vaultAudit.unencryptedCount} Credentials to AES-256`}</span>
                      </button>
                    </div>
                  )}

                  {migrationDone && (
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center gap-2">
                      <SafeIcon icon={FiCheckCircle2} className="w-4 h-4 shrink-0" />
                      <span>All credentials have been successfully encrypted with client-side AES-256-GCM!</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
